import { useModel } from '@umijs/max';
import type { TabsProps } from 'antd';
import { Tabs } from 'antd';
import { useEffect, useMemo } from 'react';
import { ActiveLanguageContext } from './context';

export interface DynaLangTabContainerProps {
  children?: React.ReactNode;
  tabsProps?: Omit<TabsProps, 'items' | 'activeKey' | 'onChange'>;
}

const DynaLangTabContainer = ({
  children,
  tabsProps,
}: DynaLangTabContainerProps) => {
  const {
    languages,
    currentLanguageCode,
    initialized,
    fetchLanguages,
    setLanguageByCode,
  } = useModel('globalLanguage');

  useEffect(() => {
    if (!initialized) {
      fetchLanguages();
    }
  }, [fetchLanguages, initialized]);

  const activeLanguageCode = useMemo(
    () => currentLanguageCode || languages[0]?.code,
    [currentLanguageCode, languages],
  );

  const activeLanguage = useMemo(
    () => languages.find((item) => item.code === activeLanguageCode),
    [activeLanguageCode, languages],
  );

  const tabItems = useMemo(
    () =>
      languages.map((item) => ({
        key: item.code,
        label: `${item.name}(${item.code})`,
      })),
    [languages],
  );

  const contextValue = useMemo(
    () => ({ activeLanguageCode, activeLanguage, languages }),
    [activeLanguageCode, activeLanguage, languages],
  );

  return (
    <ActiveLanguageContext.Provider value={contextValue}>
      <Tabs
        {...tabsProps}
        activeKey={activeLanguageCode}
        onChange={setLanguageByCode}
        items={tabItems}
      />
      {children}
    </ActiveLanguageContext.Provider>
  );
};

export default DynaLangTabContainer;
